import dayjs from 'dayjs';
import { useEffect } from 'react';

import { UserGradeResponseType, UserGradeSummaryResponseType, UserGradeStandardType } from '../types';
import { setDateFormat } from '../../../../../../lib/moment';
import { DATE_FORMAT } from '../../../../../../constants/service/common/utilConstants';
import { setNumberWithComma } from '../../../../../../lib/common';
import { getStandardDescription, getStandardMinMax } from '../helpers';
import { useUserGradesQuery } from '../queries/useUserGradesQuery';
import { useUserGradeSummaryQuery } from '../queries/useUserGradeSummaryQuery';
import ToastMsg from '../../../../../../lib/toastMsg';
import accountUserToastMsg from '../../../../../../constants/service/toastMsg/accountUserToastMsg';

export interface IUserGradeInfoViewModel {
  //유저의 현재 등급명
  currentGrade: string;
  //다음달 예상 등급명
  nextGrade: string;
  //현재 등급 산출 기간
  currentGradePeriod: string;
  //다음달 예상 등급 산출 기간
  nextGradePeriod: string;
  //다음달 예상 등급 산출 기간동안 구매 개수
  purchaseCountInDuration: string;
  //다음달 예상 등급 산출 기간동안 구매 금액
  purchaseAmountInDuration: string;
  //등급별 구매 횟수 기준
  purchaseCountStandards: UserGradeStandardType[];
  //등급별 구매 금액 기준
  purchaseAmountStandards: UserGradeStandardType[];
}

export const useUserGradeInfoViewModel = (userId: number, isActive: boolean): IUserGradeInfoViewModel => {
  const {
    data: userGrades,
    isSuccess: isUserGradesFetchSucceess,
    isError: isUserGradesFetchError,
  } = useUserGradesQuery<UserGradeResponseType[]>({
    options: {
      enabled: !!userId && isActive,
      select: (result) => result.userGrades,
    },
  });

  const {
    data: userGradeSummary,
    isSuccess: isUserGradeSummaryFetchSuccess,
    isError: isUserGradeSummaryFetchError,
  } = useUserGradeSummaryQuery<UserGradeSummaryResponseType>({
    userId,
    options: {
      enabled: !!userId && isActive,
    },
  });

  //등급 요약 정보나 회원 등급 리스트 조회에 대한 각각의 메세지는 사용자에게 의미있는 메세지가 아니기 때문에 통합해서 하나의 메세지를 노출
  const isAllFetchSuccess = isUserGradesFetchSucceess && isUserGradeSummaryFetchSuccess;
  const isFetchFail = isUserGradesFetchError || isUserGradeSummaryFetchError;

  useEffect(() => {
    if (isAllFetchSuccess) ToastMsg.success(accountUserToastMsg.GET_USER_DETAIL_GRADE_INFO_REQUEST_SUCCESS);
  }, [isAllFetchSuccess]);

  useEffect(() => {
    if (isFetchFail) ToastMsg.error(accountUserToastMsg.GET_USER_DETAIL_GRADE_INFO_REQUEST_ERROR);
  }, [isFetchFail]);

  /**
   * 유저의 다음달 예상 등급 계산
   * 1. api를 통해 가져온 등급 리스트에서 각 등급의 기준치 파악(purchaseAmount - 구매 금액 기준,purchaseCount - 구매 횟수 기준)
   * 2. 높은 등급 부터 차례대로 순회하면서 현재 유저의 구매 금액, 구매 횟수와 비교하여 기준에 부합하는 등급 리턴
   * */
  const currentUserPurchaseAmount = userGradeSummary?.recentPurchase.purchaseAmount || 0;
  const currentUserPurchaseCount = userGradeSummary?.recentPurchase.purchaseCount || 0;

  const nextGrade = Array.from(userGrades || [])
    .sort((item1, item2) => item2.grade - item1.grade)
    .find((item) => item.purchaseAmount <= currentUserPurchaseAmount && item.purchaseCount <= currentUserPurchaseCount);

  return {
    //다음 달 등급명
    nextGrade: nextGrade?.name || '-',
    //등급별 구매 횟수 기준
    purchaseCountStandards:
      userGrades?.map((grade) => {
        const { min, max } = getStandardMinMax(userGrades, grade, 'purchaseCount');
        return {
          gradeName: grade.name,
          standard: getStandardDescription(min, max, '개'),
        };
      }) || [],
    //등급별 구매 금액 기준
    purchaseAmountStandards:
      userGrades?.map((grade) => {
        const { min, max } = getStandardMinMax(userGrades, grade, 'purchaseAmount');
        return {
          gradeName: grade.name,
          standard: getStandardDescription(min, max, '원'),
        };
      }) || [],
    //현재 등급명
    currentGrade: userGradeSummary?.userGrade.name || '-',
    //현재 등급 산출 기간
    currentGradePeriod: userGradeSummary
      ? `${setDateFormat(
          dayjs(userGradeSummary?.recentPurchase.startDate).subtract(1, 'month').startOf('month'),
          DATE_FORMAT.YMD_DOT_FORMAT,
        )} ~ ${setDateFormat(
          dayjs(userGradeSummary?.recentPurchase.endDate).subtract(1, 'month').endOf('month'),
          DATE_FORMAT.YMD_DOT_FORMAT,
        )}`
      : '-',
    //다음 등급 산출 기간
    nextGradePeriod: userGradeSummary
      ? `${setDateFormat(
          dayjs(userGradeSummary?.recentPurchase.startDate).startOf('month'),
          DATE_FORMAT.YMD_DOT_FORMAT,
        )} ~ ${setDateFormat(
          dayjs(userGradeSummary?.recentPurchase.endDate).endOf('month'),
          DATE_FORMAT.YMD_DOT_FORMAT,
        )}`
      : '-',
    //기간 내 구매 횟수
    purchaseCountInDuration: setNumberWithComma(userGradeSummary?.recentPurchase.purchaseCount, '개'),
    //기간 내 구매 금액
    purchaseAmountInDuration: setNumberWithComma(userGradeSummary?.recentPurchase.purchaseAmount, '원'),
  };
};
